/**
 * Image Widget (Story 5.25, Task 3)
 *
 * Renders an uploaded image with configurable fit mode, alignment, and caption.
 * Three states: empty (upload placeholder), loading (skeleton), loaded (image).
 * Alt text applied for accessibility. Caption renders below in muted text.
 */

import { useState, useEffect } from "react";
import { Image as ImageIcon } from "@phosphor-icons/react";
import type { WidgetConfig } from "../../../lib/templateUtils";

interface ImageWidgetProps {
  widget: WidgetConfig;
}

type FitMode = "contain" | "cover" | "fill";
type AlignMode = "left" | "center" | "right";

const FIT_CLASSES: Record<FitMode, string> = {
  contain: "object-contain",
  cover: "object-cover",
  fill: "object-fill",
};

const ALIGN_CLASSES: Record<AlignMode, string> = {
  left: "justify-start text-left",
  center: "justify-center text-center",
  right: "justify-end text-right",
};

const IMG_ALIGN_CLASSES: Record<AlignMode, string> = {
  left: "mr-auto",
  center: "mx-auto",
  right: "ml-auto",
};

export function ImageWidget({ widget }: ImageWidgetProps) {
  const config = widget.config as {
    imageUrl?: string;
    alt?: string;
    fit?: FitMode;
    align?: AlignMode;
    caption?: string;
  };

  const imageUrl = config.imageUrl ?? "";
  const alt = config.alt ?? "";
  const fit = config.fit ?? "contain";
  const align = config.align ?? "center";
  const caption = config.caption ?? "";

  const [isLoading, setIsLoading] = useState(!!imageUrl);
  const [hasError, setHasError] = useState(false);

  // M2: Reset loading/error state when imageUrl changes (e.g., image replaced)
  useEffect(() => {
    setHasError(false);
    setIsLoading(!!imageUrl);
  }, [imageUrl]);

  // Empty state — no image uploaded
  if (!imageUrl) {
    return (
      <div
        data-testid="widget-type-image"
        className="flex h-full flex-col items-center justify-center p-4 text-muted-foreground"
      >
        <ImageIcon size={32} className="mb-2 opacity-50" />
        <span className="text-[13.5px]" data-testid="image-upload-placeholder">
          Click to upload or drag an image
        </span>
      </div>
    );
  }

  // Error state — image failed to load
  if (hasError) {
    return (
      <div
        data-testid="widget-type-image"
        className="flex h-full flex-col items-center justify-center p-4 text-muted-foreground"
      >
        <ImageIcon size={32} className="mb-2 opacity-30" />
        <span className="text-[13.5px]" data-testid="image-error">
          Image failed to load
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="widget-type-image"
      className={`flex h-full flex-col p-4 ${ALIGN_CLASSES[align]}`}
    >
      <div className="relative flex min-h-0 flex-1 items-center">
        {isLoading && (
          <div
            data-testid="image-loading"
            className="absolute inset-0 animate-pulse rounded-md bg-muted"
          />
        )}
        <img
          src={imageUrl}
          alt={alt}
          data-testid="image-preview"
          className={`max-h-full max-w-full ${FIT_CLASSES[fit]} ${IMG_ALIGN_CLASSES[align]} rounded-md ${isLoading ? "opacity-0" : "opacity-100"}`}
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setHasError(true);
          }}
        />
      </div>
      {caption && (
        <p
          data-testid="image-caption"
          className="mt-2 text-xs text-muted-foreground"
        >
          {caption}
        </p>
      )}
    </div>
  );
}
